import { Injectable } from '@angular/core';
import { BehaviorSubject, interval, Subscription } from 'rxjs';

export interface TimerValue {
  hours: string;
  mins: string;
  seconds: string;
  totalSeconds: number;
}

@Injectable({
  providedIn: 'root',
})
export class TimerService {
  private startTime: Date | null = null;
  private pausedTime: number = 0;
  private isPaused: boolean = false;
  private timerSubscription: Subscription | null = null;
  private timerValueSubject = new BehaviorSubject<TimerValue>({
    hours: '00',
    mins: '00',
    seconds: '00',
    totalSeconds: 0,
  });

  public timerValue$ = this.timerValueSubject.asObservable();

  get timerValue(): TimerValue {
    return this.timerValueSubject.value;
  }

  startTimer(startDate?: Date): void {
    this.startTime = startDate || new Date();
    this.isPaused = false;
    this.pausedTime = 0;

    this.timerSubscription = interval(1000).subscribe(() => {
      if (!this.isPaused && this.startTime) {
        const now = new Date();
        const elapsedSeconds =
          Math.floor((now.getTime() - this.startTime.getTime()) / 1000) -
          this.pausedTime;
        this.updateTimerValue(elapsedSeconds);
      }
    });
  }

  pauseTimer(): void {
    if (!this.isPaused && this.startTime) {
      this.isPaused = true;
      const now = new Date();
      const currentElapsed =
        Math.floor((now.getTime() - this.startTime.getTime()) / 1000) -
        this.pausedTime;
      this.pausedTime =
        Math.floor((now.getTime() - this.startTime.getTime()) / 1000) -
        currentElapsed;
    }
  }

  resumeTimer(): void {
    this.isPaused = false;
  }

  stopTimer(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }
    this.startTime = null;
    this.pausedTime = 0;
    this.isPaused = false;
    this.updateTimerValue(0);
  }

  private updateTimerValue(totalSeconds: number): void {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const timerValue: TimerValue = {
      hours: hours.toString().padStart(2, '0'),
      mins: minutes.toString().padStart(2, '0'),
      seconds: seconds.toString().padStart(2, '0'),
      totalSeconds,
    };

    this.timerValueSubject.next(timerValue);
  }

  getFormattedDuration(): string {
    const value = this.timerValue;
    return `${value.hours}:${value.mins}:${value.seconds}`;
  }
}
